/**
 * Station impacts (M2 spec §7.2 step 4): hull damage `max(0, impactSpeed − impact.freeSpeed) × impact.damagePerMps`.
 *
 * The impact speed is the part of a tick's velocity change that the flight model cannot explain (plan D-TBD:
 * collisions are read from the velocity change, not from Rapier contact events). Two forms:
 * - **Bound form** (the plan's): `impactSpeed(vPre, vPost, maxThrustAccel(def), dt)` with the raw pre-step
 *   velocity. Needs nothing but the def; it under-reads every impact by up to `maxThrustAccel × dt` (about
 *   2 m/s for the Kestrel).
 * - **Exact form**: predict this tick's thrust with `thrustAcceleration` (from the `Thrust` output the flight step
 *   wrote), pass `vPre + a × dt` as `vPre` and `maxAccel = 0`. A 30 m/s head-on stop then reads 30 m/s.
 * Both read 0 for free flight at any thrust, boost included. Allocation-free (D-014).
 */
import type { Quat, Vec3 } from '../core/math'
import type { DockingConfig } from '../data/m2Schemas'
import type { ShipDef } from '../data/schemas'

type ThrustDef = Pick<ShipDef, 'mass' | 'thrustForward' | 'thrustReverse' | 'thrustLateral' | 'thrustVertical' | 'boostThrustMult'>

/**
 * The largest linear acceleration the ship's thrusters can produce (m/s²): every axis at its limit at once,
 * forward boosted, i.e. `|(max(forward × boost, reverse), lateral, vertical)| / mass`.
 */
export function maxThrustAccel(def: ThrustDef): number {
  const axial = Math.max(def.thrustForward * def.boostThrustMult, def.thrustReverse)
  return Math.sqrt(axial * axial + def.thrustLateral * def.thrustLateral + def.thrustVertical * def.thrustVertical) / def.mass
}

/**
 * The impact check's floats, passed and returned through a number-initialised class (DA-1 follow-up, D-TBD; D-1049's
 * `LeadArgs`): a float passed to or returned from a call V8 does not inline is boxed (16 B), one in a class field is not.
 * In: `maxAccel`, `dt` (`impactSpeedTo`); out: `speed` (`impactSpeedTo`, then `impactDamageTo`'s input), `damage`.
 */
export class ImpactFloats {
  maxAccel = 0
  dt = 0
  speed = 0
  damage = 0
}

/** `impactSpeed` into `k.speed` from `k.maxAccel` and `k.dt` (bit for bit). Allocation-free. */
export function impactSpeedTo(vPre: Readonly<Vec3>, vPost: Readonly<Vec3>, k: ImpactFloats): void {
  const dvx = vPost.x - vPre.x
  const dvy = vPost.y - vPre.y
  const dvz = vPost.z - vPre.z
  const dv = Math.sqrt(dvx * dvx + dvy * dvy + dvz * dvz) - k.maxAccel * k.dt
  if (dv > 0) k.speed = dv
  else k.speed = 0
}

/** `impactDamage` of `k.speed` into `k.damage` (bit for bit). Allocation-free. */
export function impactDamageTo(cfg: Pick<DockingConfig, 'impact'>, k: ImpactFloats): void {
  const over = k.speed - cfg.impact.freeSpeed
  if (over > 0) k.damage = over * cfg.impact.damagePerMps
  else k.damage = 0
}

const wrapFloats = new ImpactFloats()

/** `max(0, |vPost − vPre| − maxAccel × dt)`: the velocity change (m/s) thrust cannot account for. */
export function impactSpeed(vPre: Readonly<Vec3>, vPost: Readonly<Vec3>, maxAccel: number, dt: number): number {
  wrapFloats.maxAccel = maxAccel
  wrapFloats.dt = dt
  impactSpeedTo(vPre, vPost, wrapFloats)
  return wrapFloats.speed
}

/** Hull damage of an impact at `speed` m/s: `max(0, speed − freeSpeed) × damagePerMps`. */
export function impactDamage(speed: number, cfg: Pick<DockingConfig, 'impact'>): number {
  wrapFloats.speed = speed
  impactDamageTo(cfg, wrapFloats)
  return wrapFloats.damage
}

/**
 * The world acceleration (m/s²) of this tick's thrust, rebuilt from the flight step's `Thrust` output (each
 * axis a signed fraction of its limit; surge ≥ 0 uses the forward limit, boosted while `boosting`, surge < 0 the
 * reverse limit) and the ship's rotation at the flight step (the pre-step `Rot`). Writes into `out`.
 */
export function thrustAcceleration(
  def: ThrustDef,
  rot: Readonly<Quat>,
  surge: number,
  sway: number,
  heave: number,
  boosting: boolean,
  out: Vec3,
): Vec3 {
  wrapThrust.surge = surge
  wrapThrust.sway = sway
  wrapThrust.heave = heave
  wrapThrust.boosting = boosting
  return thrustAccelerationFrom(def, rot, wrapThrust, out)
}

/** A tick's `Thrust` output as `thrustAccelerationFrom` reads it (a class, so its float fields stay unboxed). */
export class ThrustSample {
  surge = 0
  sway = 0
  heave = 0
  boosting = false
}

const wrapThrust = new ThrustSample()

/**
 * `thrustAcceleration` with the thrust read from `t`, the rotation written out (`rotateRaw`'s expression, bit for
 * bit). Only objects cross the call, and not `rotateVecFrom` either: inlined here with its game-wide feedback it
 * boxed 2 floats per tick on the station rig (DA-1 follow-up, D-TBD; D-1116's `noseUpInto`). Allocation-free.
 */
export function thrustAccelerationFrom(def: ThrustDef, rot: Readonly<Quat>, t: Readonly<ThrustSample>, out: Vec3): Vec3 {
  const surge = t.surge
  const forward = def.thrustForward * (t.boosting ? def.boostThrustMult : 1)
  const lz = surge >= 0 ? -surge * forward : -surge * def.thrustReverse
  const vx = (t.sway * def.thrustLateral) / def.mass
  const vy = (t.heave * def.thrustVertical) / def.mass
  const vz = lz / def.mass
  const qx = rot.x
  const qy = rot.y
  const qz = rot.z
  const qw = rot.w
  const tx = 2 * (qy * vz - qz * vy)
  const ty = 2 * (qz * vx - qx * vz)
  const tz = 2 * (qx * vy - qy * vx)
  out.x = vx + qw * tx + (qy * tz - qz * ty)
  out.y = vy + qw * ty + (qz * tx - qx * tz)
  out.z = vz + qw * tz + (qx * ty - qy * tx)
  return out
}
